'use client';

// https://21st.dev/ibelick/dock/default
import { c as _c } from "react/compiler-runtime";
import { m, MotionValue, useMotionValue, useMotionValueEvent, useSpring, useTransform, type SpringOptions, AnimatePresence } from 'motion/react';
import { DURATION, EASE_OUT_STRONG } from '@/lib/motion-utils';
import { Children, createContext, use, useRef, useState } from 'react';
import React from 'react';
import { cn } from '@/lib/utils';
const DOCK_HEIGHT = 128;
const DEFAULT_MAGNIFICATION = 80;
const DEFAULT_DISTANCE = 150;
const DEFAULT_PANEL_HEIGHT = 64;
type DockProps = {
  children: React.ReactNode;
  className?: string;
  distance?: number;
  panelHeight?: number;
  magnification?: number;
  spring?: SpringOptions;
};
type DockItemProps = {
  className?: string;
  children: React.ReactNode;
};
type DockLabelProps = {
  className?: string;
  children: React.ReactNode;
};
type DockIconProps = {
  className?: string;
  children: React.ReactNode;
};
type DocContextType = {
  mouseX: MotionValue;
  spring: SpringOptions;
  magnification: number;
  distance: number;
};
type DockProviderProps = {
  children: React.ReactNode;
  value: DocContextType;
};
const DockContext = createContext<DocContextType | undefined>(undefined);
function DockProvider(t0) {
  const $ = _c(3);
  const {
    children,
    value
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <DockContext.Provider value={value}>{children}</DockContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function useDock() {
  const context = use(DockContext);
  if (!context) {
    throw new Error('useDock must be used within an DockProvider');
  }
  return context;
}
function Dock(t0) {
  const $ = _c(33);
  const {
    children,
    className,
    spring: t1,
    magnification: t2,
    distance: t3,
    panelHeight: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? {
      mass: 0.1,
      stiffness: 150,
      damping: 12
    } : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const spring = t5;
  const magnification = t2 === undefined ? DEFAULT_MAGNIFICATION : t2;
  const distance = t3 === undefined ? DEFAULT_DISTANCE : t3;
  const panelHeight = t4 === undefined ? DEFAULT_PANEL_HEIGHT : t4;
  const mouseX = useMotionValue(Infinity);
  const isHovered = useMotionValue(0);
  const maxHeight = Math.max(DOCK_HEIGHT, magnification + magnification / 2 + 4);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [0, 1];
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== maxHeight || $[4] !== panelHeight) {
    t7 = [panelHeight, maxHeight];
    $[3] = maxHeight;
    $[4] = panelHeight;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const heightRow = useTransform(isHovered, t6, t7);
  const height = useSpring(heightRow, spring);
  let t8;
  if ($[6] !== height) {
    t8 = {
      height,
      scrollbarWidth: "none"
    };
    $[6] = height;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t10;
  let t9;
  if ($[8] !== isHovered || $[9] !== mouseX) {
    t9 = t11 => {
      const {
        pageX
      } = t11;
      isHovered.set(1);
      mouseX.set(pageX);
    };
    t10 = () => {
      isHovered.set(0);
      mouseX.set(Infinity);
    };
    $[8] = isHovered;
    $[9] = mouseX;
    $[10] = t10;
    $[11] = t9;
  } else {
    t10 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[12] !== className) {
    t11 = cn("mx-auto flex w-fit gap-4 overflow-visible rounded-2xl bg-neutral-50 px-4 dark:bg-neutral-900", className);
    $[12] = className;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== panelHeight) {
    t12 = {
      height: panelHeight
    };
    $[14] = panelHeight;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== distance || $[17] !== magnification || $[18] !== mouseX || $[19] !== spring) {
    t13 = {
      mouseX,
      spring,
      distance,
      magnification
    };
    $[16] = distance;
    $[17] = magnification;
    $[18] = mouseX;
    $[19] = spring;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== children || $[22] !== t13) {
    t14 = <DockProvider value={t13}>{children}</DockProvider>;
    $[21] = children;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t14 || $[28] !== t9) {
    t15 = <m.div onMouseMove={t9} onMouseLeave={t10} className={t11} style={t12} role="toolbar" aria-label="Application dock">{t14}</m.div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t14;
    $[28] = t9;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t8) {
    t16 = <m.div style={t8} className="mx-2 flex max-w-full items-end overflow-visible">{t15}</m.div>;
    $[30] = t15;
    $[31] = t8;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}
function DockItem(t0) {
  const $ = _c(30);
  const {
    children,
    className
  } = t0;
  const ref = useRef(null);
  const {
    distance,
    magnification,
    mouseX,
    spring
  } = useDock();
  const isHovered = useMotionValue(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = val => {
      const domRect = ref.current?.getBoundingClientRect() ?? {
        x: 0,
        width: 0
      };
      return val - domRect.x - domRect.width / 2;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mouseDistance = useTransform(mouseX, t1);
  const t2 = -distance;
  let t3;
  if ($[1] !== distance || $[2] !== t2) {
    t3 = [t2, 0, distance];
    $[1] = distance;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== magnification) {
    t4 = [40, magnification, 40];
    $[4] = magnification;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const widthTransform = useTransform(mouseDistance, t3, t4);
  const width = useSpring(widthTransform, spring);
  let t5;
  if ($[6] !== width) {
    t5 = {
      width
    };
    $[6] = width;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== isHovered) {
    t6 = () => isHovered.set(1);
    t7 = () => isHovered.set(0);
    t8 = () => isHovered.set(1);
    t9 = () => isHovered.set(0);
    $[8] = isHovered;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] !== className) {
    t10 = cn("relative inline-flex items-center justify-center active:scale-[0.97]", className);
    $[13] = className;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== children || $[16] !== isHovered || $[17] !== width) {
    let t12;
    if ($[19] !== isHovered || $[20] !== width) {
      t12 = child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        if (typeof child.type === "string") {
          return child;
        }
        if (child.type !== DockIcon && child.type !== DockLabel) {
          return child;
        }
        return React.cloneElement(child as React.ReactElement<{
          width?: MotionValue<number>;
          isHovered?: MotionValue<number>;
        }>, {
          width,
          isHovered
        });
      };
      $[19] = isHovered;
      $[20] = width;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t11 = Children.map(children, t12);
    $[15] = children;
    $[16] = isHovered;
    $[17] = width;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t12 = <m.div ref={ref} style={t5} onHoverStart={t6} onHoverEnd={t7} onFocus={t8} onBlur={t9} className={t10} tabIndex={0} role="button" aria-haspopup="true">{t11}</m.div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function DockLabel(t0) {
  const $ = _c(11);
  let children;
  let className;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  const restProps = rest as Record<string, unknown>;
  const isHovered = restProps.isHovered as MotionValue<number>;
  const [isVisible, setIsVisible] = useState(false);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = latest => {
      setIsVisible(latest === 1);
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useMotionValueEvent(isHovered, "change", t1);
  let t2;
  if ($[5] !== children || $[6] !== className || $[7] !== isVisible) {
    t2 = isVisible && <m.div initial={{
      opacity: 0,
      y: 4
    }} animate={{
      opacity: 1,
      y: -10
    }} exit={{
      opacity: 0,
      y: 4
    }} transition={{
      duration: DURATION.tooltip,
      ease: EASE_OUT_STRONG
    }} className={cn("absolute -top-6 left-1/2 w-fit rounded-md border border-neutral-200 bg-neutral-100 px-2 py-0.5 text-xs whitespace-pre text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white", className)} role="tooltip" style={{
      x: "-50%"
    }}>{children}</m.div>;
    $[5] = children;
    $[6] = className;
    $[7] = isVisible;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <AnimatePresence>{t2}</AnimatePresence>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function DockIcon(t0) {
  const $ = _c(12);
  let children;
  let className;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  const restProps = rest as Record<string, unknown>;
  const width = restProps.width as MotionValue<number>;
  const widthTransform = useTransform(width, _temp);
  let t1;
  if ($[4] !== widthTransform) {
    t1 = {
      width: widthTransform
    };
    $[4] = widthTransform;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className) {
    t2 = cn("flex items-center justify-center", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== t1 || $[10] !== t2) {
    t3 = <m.div style={t1} className={t2}>{children}</m.div>;
    $[8] = children;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function _temp(val) {
  return val / 2;
}
export { Dock, DockIcon, DockItem, DockLabel };